import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatCurrencyINR } from '../../../utils/currency';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';

const AssetTable = ({ assets, onEditAsset, onDeleteAsset, selectedCategory = 'All' }) => {
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const [sortField, setSortField] = useState('currentValue');
  const [sortDirection, setSortDirection] = useState('desc');

  const filteredAssets = selectedCategory === 'All'
    ? assets
    : selectedCategory === 'FD & Cash'
      ? assets.filter(asset => asset.type === 'Fixed Deposits' || asset.type === 'Cash')
      : assets.filter(asset => asset.type === selectedCategory);

  const sortedAssets = [...filteredAssets].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    if (sortField === 'currentValue' || sortField === 'expectedReturn') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    }
    
    if (sortField === 'acquisitionDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getLiquidityBadge = (liquidity) => {
    const isLiquid = liquidity === 'Liquid';
    return (
      <span className={`
        inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
        ${isLiquid 
          ? 'bg-accent/10 text-accent' :'bg-warning/10 text-warning'
        }
      `}>
        <Icon 
          name={isLiquid ? "Droplets" : "Lock"} 
          size={12} 
        />
        {liquidity}
      </span>
    );
  };

  const getRiskBadge = (probability) => {
    let riskLevel = 'Low';
    let colorClass = 'bg-accent/10 text-accent';
    
    if (probability < 70) {
      riskLevel = 'High';
      colorClass = 'bg-destructive/10 text-destructive';
    } else if (probability < 85) {
      riskLevel = 'Medium';
      colorClass = 'bg-warning/10 text-warning';
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        {riskLevel}
      </span>
    );
  };

  const SortHeader = ({ field, children }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        <Icon 
          name={sortField === field 
            ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown')
            : 'ChevronsUpDown'
          } 
          size={14} 
          className={sortField === field ? 'text-primary' : 'text-muted-foreground'} 
        />
      </div>
    </th>
  );

  if (filteredAssets.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-8">
        <div className="text-center">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Assets Found</h3>
          <p className="text-muted-foreground mb-4">
            {selectedCategory === 'All' ?'Start building your portfolio by adding your first asset'
              : `No ${selectedCategory.toLowerCase()} assets found in your portfolio`
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <SortHeader field="name">Asset Name</SortHeader>
              <SortHeader field="type">Type</SortHeader>
              <SortHeader field="currentValue">Current Value</SortHeader>
              <SortHeader field="acquisitionDate">Acquired</SortHeader>
              <SortHeader field="expectedReturn">Expected Return</SortHeader>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Liquidity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Risk Level
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedAssets.map((asset) => (
              <tr key={asset.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon 
                        name={
                          asset.type === 'Equity' ? 'TrendingUp' :
                          asset.type === 'Mutual Funds' ? 'PieChart' :
                          asset.type === 'Real Estate' ? 'Home' :
                          asset.type === 'PPF' ? 'Shield' :
                          (asset.type === 'Fixed Deposits' || asset.type === 'Cash') ? 'Banknote' : 'Coins'
                        } 
                        size={16} 
                        className="text-primary" 
                      />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{asset.name}</p>
                      {asset.description && (
                        <p className="text-sm text-muted-foreground">{asset.description}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">{asset.type}</span>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <p className="font-medium text-foreground">
                      {formatCurrencyINR(asset.currentValue)}
                    </p>
                    {asset.purchaseValue && (
                      <p className="text-sm text-muted-foreground">
                        Cost: {formatCurrencyINR(asset.purchaseValue)}
                      </p>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">
                    {new Date(asset.acquisitionDate).toLocaleDateString()}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-foreground">
                      {asset.expectedReturn}%
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({asset.probability}% confidence)
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  {getLiquidityBadge(asset.liquidity)}
                </td>
                <td className="px-4 py-4">
                  {getRiskBadge(asset.probability)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditAsset(asset)}
                      className="h-8 w-8 disabled:opacity-60"
                      disabled={!effectiveCanWrite || asset.type === 'Emergency Fund'}
                      title={!effectiveCanWrite ? (isDemoModeActive ? 'Cannot edit in demo mode' : 'Upgrade to edit') : (asset.type === 'Emergency Fund' ? 'Managed automatically' : 'Edit')}
                    >
                      <Icon name="Edit2" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteAsset(asset.id)}
                      className="h-8 w-8 text-destructive hover:text-destructive disabled:opacity-60"
                      disabled={!effectiveCanWrite || asset.type === 'Emergency Fund'}
                      title={!effectiveCanWrite ? 'Cannot delete in demo mode' : (asset.type === 'Emergency Fund' ? 'Managed automatically' : 'Delete')}
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssetTable;